import { useState } from "react";
import Components from "../../components";
import { useNavigate } from "react-router-dom";
import ikas from "../../providers/ikas";
import * as compSettings from "./settings";
import config from "../../config";
import { CaretRight } from "@phosphor-icons/react";

const Cart = ({ id, extendedSettings, data, dataSetter }) => {
  const settings = {
    ...compSettings,
    ...extendedSettings?.[id],
  };

  const [loading, setLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState("success"); // success | error
  const [showEmptyCartModal, setShowEmptyCartModal] = useState(false);
  const navigate = useNavigate;

  // Toast message function
  const showToastMessage = (message, type = "success") => {
    setToast(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
      setTimeout(() => {
        setToast("");
      }, 400); // match exit animation duration
    }, 2000); // visible duration
  };

  const items = data.cart?.orderLineItems || data?.cart?.items || [];
  const subTotal = items
    .map((item) => item.price * item.quantity)
    .reduce((acc, curr) => acc + curr, 0);

  // Cart update handler
  const handleCartUpdate = async (updatedCart) => {
    dataSetter({ ...data, cart: updatedCart });
  };

  // Coupon functions
  const applyCoupon = async (couponCode) => {
    setCouponLoading(true);
    try {
      const result = await ikas.saveCart({
        id: data?.cart?.id,
        couponCode: couponCode,
      });
      dataSetter(result);
    } catch (error) {
      console.error("Kupon kodu hatası:", error);
      showToastMessage("Kupon kodu uygulanırken hata oluştu", "error");
      return { success: false, error };
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = async () => {
    setCouponLoading(true);
    try {
      const result = await ikas.saveCart({
        id: data?.cart?.id,
        couponCode: "",
      });
      dataSetter(result);
    } catch (error) {
      console.error("Kupon kodu kaldırma hatası:", error);
      showToastMessage("Kupon kodu kaldırılırken hata oluştu", "error");
    } finally {
      setCouponLoading(false);
    }
  };

  // Note functions
  const applyNote = async (noteText) => {
    setNoteLoading(true);
    try {
      const result = await ikas.saveCart({
        id: data?.cart?.id,
        note: noteText,
      });
      dataSetter(result);
    } catch (error) {
      console.error("Sipariş notu hatası:", error);
      showToastMessage("Sipariş notu eklenirken hata oluştu", "error");
      return { success: false, error };
    } finally {
      setNoteLoading(false);
    }
  };

  const removeNote = async () => {
    setNoteLoading(true);
    try {
      const result = await ikas.saveCart({
        id: data?.cart?.id,
        note: "",
      });
      dataSetter(result);
    } catch (error) {
      console.error("Sipariş notu kaldırma hatası:", error);
      showToastMessage("Sipariş notu kaldırılırken hata oluştu", "error");
      return { success: false, error };
    } finally {
      setNoteLoading(false);
    }
  };

  async function handleDecrement(item) {
    setLoading(true);
    try {
      const input = {
        cartId: data?.cart?.id,
        salesChannelId: config.SALES_CHANNEL_ID,
        storefrontThemeId: config.ROUTING_ID,
        item: {
          id: item.id,
          quantity: item.quantity - 1,
          variant: {
            id: item.variant.id,
            name: item.variant.name,
          },
        },
      };
      const result = await ikas.addItemToCart(input);
      dataSetter(result);
    } finally {
      setLoading(false);
    }
  }

  async function handleIncrement(item) {
    setLoading(true);
    try {
      const input = {
        cartId: data?.cart?.id,
        salesChannelId: config.SALES_CHANNEL_ID,
        storefrontThemeId: config.ROUTING_ID,
        item: {
          id: item.id,
          quantity: item.quantity + 1,
          variant: {
            id: item.variant.id,
            name: item.variant.name,
          },
        },
      };
      const result = await ikas.addItemToCart(input);
      dataSetter(result);
    } finally {
      setLoading(false);
    }
  }

  async function handleRemove(item) {
    setLoading(true);
    try {
      const input = {
        cartId: data?.cart?.id,
        salesChannelId: config.SALES_CHANNEL_ID,
        storefrontThemeId: config.ROUTING_ID,
        item: {
          id: item.id,
          quantity: 0,
          variant: {
            id: item.variant.id,
            name: item.variant.name,
          },
        },
      };
      const result = await ikas.addItemToCart(input);
      dataSetter(result);
    } finally {
      setLoading(false);
    }
  }

  async function handleEmptyCart() {
    // Show confirmation modal
    setShowEmptyCartModal(true);
  }

  async function confirmEmptyCart() {
    setShowEmptyCartModal(false);
    setLoading(true);
    try {
      dataSetter({
        key: "cart",
        value: {},
      });
      localStorage.removeItem("cartId");
      showToastMessage("Sepet başarıyla boşaltıldı", "success");
    } catch (error) {
      console.error("Sepet boşaltma hatası:", error);
      showToastMessage("Sepet boşaltılırken hata oluştu", "error");
    } finally {
      setLoading(false);
    }
  }

  function cancelEmptyCart() {
    setShowEmptyCartModal(false);
  }

  function handleDiscover() {
    navigate("/");
  }
  const onCheckoutClick = () => {
    ikas.checkout(data);
  };

  return (
    <div className="flex flex-col overflow-hidden min-h-screen bg-white">
      {toast && (
        <div
          className={`fixed left-1/2 transform -translate-x-1/2 z-[100] w-[95vw] max-w-xl px-6 py-3 rounded-lg shadow-lg text-base font-semibold text-center transition-all duration-400
            ${
              showToast
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-4 pointer-events-none"
            }`}
          style={{
            minWidth: 320,
            top: data?.system?.safeArea?.top ?? 20,
            backgroundColor: toastType === "error" ? "#ef4444" : "#6f59ef",
            color: "#fff",
          }}
        >
          {toast}
        </div>
      )}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white">
        <Components.CartHeader
          count={data.cart?.itemCount || 0}
          onEmptyCart={handleEmptyCart}
          safeArea={data?.system?.safeArea}
        />
      </div>
      {!loading && (data.cart?.itemCount ?? 0) === 0 && (
        <div
          className="flex-1 flex flex-col items-center justify-center px-4"
          style={{
            paddingTop: `${(data?.system?.safeArea?.top ?? 0) + 60}px`,
          }}
        >
          <h2 className="text-lg font-semibold mb-2">Sepetiniz boş</h2>
          <p className="text-gray-600 mb-6 text-center">
            Henüz sepetinize ürün eklemediniz.
          </p>
          <button
            onClick={handleDiscover}
            className="bg-black text-white px-6 py-3 rounded-lg uppercase text-sm font-medium"
          >
            Keşfet
          </button>
        </div>
      )}
      <div
        className="flex-1 overflow-y-auto scroll-container"
        style={{
          paddingTop: `${(data?.system?.safeArea?.top ?? 0) + 60}px`,
          paddingBottom: `${(data?.system?.safeArea?.bottom ?? 0) + 160}px`,
        }}
      >
        <Components.CartItem
          items={items}
          onDecrement={handleDecrement}
          onIncrement={handleIncrement}
          onRemove={handleRemove}
        />
      </div>
      {!loading && data.cart?.itemCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200">
          <div className="px-4 py-2">
            <Components.CheckoutSummary
              subTotal={subTotal?.toFixed(2)}
              total={data?.cart?.totalPrice?.toFixed(2)}
              cart={data?.cart}
              onCartUpdate={handleCartUpdate}
              applyCoupon={applyCoupon}
              removeCoupon={removeCoupon}
              applyNote={applyNote}
              removeNote={removeNote}
              couponLoading={couponLoading}
              noteLoading={noteLoading}
            />
           <button
  onClick={onCheckoutClick}
  className="w-full bg-black text-white py-3 rounded-lg uppercase text-sm font-bold flex items-center justify-center gap-0.5 mt-4 mb-4"
>
  ÖDEME YAP
  <CaretRight size={12} weight="bold" />
</button>
          </div>
        </div>
      )}

      {/* Empty Cart Confirmation Modal */}
      {showEmptyCartModal && (
        <div className="fixed inset-0 z-[200] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-lg"
            style={{ maxWidth: "320px" }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
              Sepeti Boşalt
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Sepeti boşaltmak istediğinize emin misiniz? Bu işlem geri
              alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelEmptyCart}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
              >
                İptal
              </button>
              <button
                onClick={confirmEmptyCart}
                className="flex-1 px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors duration-200 font-medium"
              >
                Boşalt
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <span className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></span>
        </div>
      )}
    </div>
  );
};

export default Cart;
